import React, { useState, useEffect } from "react";
import signalAILogo from "../../../assets/illustrations/signal-AI.png";
import { useFormik } from "formik";
import * as Yup from "yup";
import "../signal.css";
import { postLogDetails } from "../../../Services/APIs/appDetails";

const validationSchema = Yup.object().shape({
    brushingFrequency: Yup.string().required('တစ်နေ့သွားတိုက်သည့်အကြိမ်ရေ ရွေးချယ်ပါ'),
    dentalCheckupFrequency: Yup.string().required('သွားစစ်ဆေးသည့်အကြိမ်ရေ ရွေးချယ်ပါ'),
    dentalProblem: Yup.string().required('သင့်လက်ရှိသွားပြသာနာများကိုပြောပြပါ'),
    decisionFactors: Yup.array().min(1, 'အနည်းဆိုင်တစ်တခုတ်များရွေးချယ်ပါ'),
    currentToothpaste: Yup.string().required('လက်ရှိသုံးနေသည့်သွားတိုက်ဆေးတံဆိပ်နှင့်အမျိုးအစား ရွေးချယ်ပါ'),
    gender: Yup.string().required('ကျား/မ ရွေးချယ်ပါ'),
    age: Yup.number().min(1).max(120).required('အသက်ကို ၁-၁၂၀ အတွင်းရွေးချယ်ပါ'),
    city: Yup.string().required('မြို့ကို ရွေးချယ်ပါ'),
});

export default function SignalAiCaseForm({
    storageKey = "signal_ai_case_form",
    onSubmit,
}) {
    const [currentStep, setCurrentStep] = useState(1);
    const uid = JSON.parse(localStorage.getItem("usertpi") || '{}').usertpi
    const app = JSON.parse(localStorage.getItem("companyBucket") || "{}").companyBucket

    useEffect(() => {
        postLogDetails({ uid, app, payLoad: `Questionnaire opened - step ${currentStep}` })
    }, [currentStep])

    const formik = useFormik({
        initialValues: {
            brushingFrequency: '',
            dentalCheckupFrequency: '',
            dentalProblem: '',
            decisionFactors: [],
            currentToothpaste: '',
            gender: '',
            age: '',
            city: '',
        },
        validationSchema,
        onSubmit: (values) => {
            // Touch all fields to show errors if any are empty
            const allFields = ['brushingFrequency', 'dentalCheckupFrequency', 'dentalProblem', 'decisionFactors', 'currentToothpaste', 'gender', 'age', 'city'];
            allFields.forEach(field => {
                formik.setFieldTouched(field, true);
            });

            // Validate all fields before submission
            const errors = formik.errors;
            if (Object.keys(errors).length > 0) {
                postLogDetails({ uid, app, payLoad: `Questionnaire submission failed - validation errors: ${Object.keys(errors).join(', ')}` })
                return; // Don't submit if there are errors
            }

            postLogDetails({ uid, app, payLoad: `Questionnaire submitted successfully - brushing: ${values.brushingFrequency}, checkup: ${values.dentalCheckupFrequency}, problem: ${values.dentalProblem}, factors: ${values.decisionFactors.join(', ')}, toothpaste: ${values.currentToothpaste}, gender: ${values.gender}, age: ${values.age}, city: ${values.city}` })
            localStorage.setItem(storageKey, JSON.stringify(values));
            onSubmit && onSubmit(values);
        },
    });

    const validateSection1 = () => {
        const section1Fields = ['brushingFrequency', 'dentalCheckupFrequency', 'dentalProblem', 'decisionFactors', 'currentToothpaste'];
        const errors = {};

        section1Fields.forEach(field => {
            if (field === 'decisionFactors') {
                if (formik.values.decisionFactors.length === 0) {
                    errors.decisionFactors = 'Please select at least one factor';
                }
            } else if (!formik.values[field]) {
                errors[field] = 'This field is required';
            }
        });

        // Touch all fields to show errors
        section1Fields.forEach(field => {
            formik.setFieldTouched(field, true);
        });

        if (Object.keys(errors).length > 0) {
            formik.setErrors(errors);
            return false;
        }
        return true;
    };

    const handleNext = () => {
        if (validateSection1()) {
            postLogDetails({ uid, app, payLoad: `Questionnaire step 1 completed - moving to step 2` })
            setCurrentStep(2);
        } else {
            postLogDetails({ uid, app, payLoad: `Questionnaire step 1 validation failed` })
        }
    };

    const handleBack = () => {
        postLogDetails({ uid, app, payLoad: `Questionnaire step 2 - moving back to step 1` })
        setCurrentStep(1);
    };

    const getFieldProps = (fieldName) => ({
        ...formik.getFieldProps(fieldName),
        error: formik.touched[fieldName] && formik.errors[fieldName],
        touched: formik.touched[fieldName],
    });

    return (
        <div className="signal-ai-bg-image h-full w-full absolute left-0 top-0">
            <form
                onSubmit={formik.handleSubmit}
                className="w-full h-full max-w-xl mx-auto rounded-2xl p-6 px-10 shadow-sm overflow-y-auto"
            >
                {/* Header */}
                <img src={signalAILogo} className="w-2/5 mx-auto my-5" alt="signal ai logo" />


                {/* Section 1 */}
                {currentStep === 1 && (
                    <div className="space-y-8">
                        <p className="text-signal-ai-blue font-bold mb-5">
                            Aiမှ မှန်ကန်စွာစစ်ဆေးပေးနိုင်ရန် အချက်အလက်များ မှန်ကန်စွာဖြည့်ပါ။
                        </p>

                        {/* Brushing Frequency */}
                        <div>
                            <h4 className="mb-2 font-medium text-signal-ai-blue">
                                တစ်နေ့သွားတိုက်သည့်အကြိမ်ရေ
                            </h4>
                            <div className="flex flex-wrap gap-4 justify-start">
                                {[
                                    { value: '1 time', label: '၁ကြိမ်' },
                                    { value: '2 times', label: '၂ကြိမ်' },
                                    { value: '3 times', label: '၃ကြိမ်' },
                                    { value: '4 times', label: '၄ကြိမ်' }
                                ].map((option) => (
                                    <label key={option.value} className="flex items-start gap-2">
                                        <input
                                            type="radio"
                                            name="brushingFrequency"
                                            checked={formik.values.brushingFrequency === option.value}
                                            onChange={() => formik.setFieldValue('brushingFrequency', option.value)}
                                        />
                                        <span className="text-sm">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {getFieldProps('brushingFrequency').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('brushingFrequency').error}</p>
                            )}
                        </div>

                        {/* Dental Checkup Frequency */}
                        <div>
                            <h4 className="mb-2 font-medium text-signal-ai-blue">
                                သွားစစ်ဆေးသည့်အကြိမ်ရေ
                            </h4>
                            <div className="flex flex-wrap gap-4 justify-start">
                                {[
                                    { value: '3 months', label: '၃လတစ်ခါ' },
                                    { value: '6 months', label: '၆လတစ်ခါ' },
                                    { value: '1 year', label: '၁နှစ်တစ်ခါ' },
                                    { value: 'never', label: 'မလုပ်ဖြစ်ပါ' }
                                ].map((option) => (
                                    <label key={option.value} className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="dentalCheckupFrequency"
                                            checked={formik.values.dentalCheckupFrequency === option.value}
                                            onChange={() => formik.setFieldValue('dentalCheckupFrequency', option.value)}
                                        />
                                        <span className="text-sm">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {getFieldProps('dentalCheckupFrequency').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('dentalCheckupFrequency').error}</p>
                            )}
                        </div>

                        {/* Current Dental Problem */}
                        <div>
                            <h4 className="mb-4 font-medium text-signal-ai-blue">
                                သင့်လက်ရှိသွားပြသာနာများကိုပြောပြပါ။
                            </h4>
                            <input
                                type="text"
                                className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-signal-ai-primary focus:outline-none"
                                value={formik.values.dentalProblem}
                                onChange={(e) => formik.setFieldValue('dentalProblem', e.target.value)}
                            />
                            {getFieldProps('dentalProblem').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('dentalProblem').error}</p>
                            )}
                        </div>

                        {/* Decision Factors */}
                        <div>
                            <h4 className="mb-2 font-medium text-signal-ai-blue">
                                သွားတိုက်ဆေးရွေးချယ်သည့်အချက်
                            </h4>
                            <div className="space-y-3 text-sm">
                                {[
                                    { value: 'Brand', label: 'တံဆိပ်' },
                                    { value: 'shopkeeper_recommendation', label: 'ဆိုင်ရှင်အညွှန်း' },
                                    { value: 'Price', label: 'စျေးနှုန်း' },
                                    { value: 'Recommendation', label: 'သူတစ်ပါးထောက်ခံချက် (မိတ်ဆွေ/အွန်လိုင်းပို့စ်)' },
                                    { value: 'Benefit', label: 'ကောင်းကျိုး (ဥပမာ : သွားပိုးကာကွယ်ခြင်း/သွားဖြူစေခြင်း)' }
                                ].map((factor) => (
                                    <label key={factor.value} className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={formik.values.decisionFactors.includes(factor.value)}
                                            onChange={() => {
                                                const updatedFactors = formik.values.decisionFactors.includes(factor.value)
                                                    ? formik.values.decisionFactors.filter(f => f !== factor.value)
                                                    : [...formik.values.decisionFactors, factor.value];
                                                formik.setFieldValue('decisionFactors', updatedFactors);
                                            }}
                                        />
                                        <span>{factor.label}</span>
                                    </label>
                                ))}
                            </div>
                            {getFieldProps('decisionFactors').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('decisionFactors').error}</p>
                            )}
                        </div>

                        {/* Currently Using Toothpaste */}
                        <div>
                            <h4 className="mb-4 font-medium text-signal-ai-blue">
                                လက်ရှိသုံးနေသည့်သွားတိုက်ဆေးတံဆိပ်နှင့်အမျိုးအစား (ဥပမာ: Signal Cavity Fighter)
                            </h4>
                            <input
                                type="text"
                                className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-signal-ai-primary focus:outline-none"
                                value={formik.values.currentToothpaste}
                                onChange={(e) => formik.setFieldValue('currentToothpaste', e.target.value)}
                            />
                            {getFieldProps('currentToothpaste').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('currentToothpaste').error}</p>
                            )}
                        </div>

                        {/* Step Indicator */}
                        <div className="flex items-center justify-center mt-8 mb-4">
                            <div className="flex items-center gap-4">
                                <div className={`w-3 h-3 rounded-full transition-all ${currentStep === 1 ? 'bg-signal-ai-secondary' : 'bg-gray-500'
                                    }`} />
                                <div className="w-16 h-0.5 bg-gray-500" />
                                <div className={`w-3 h-3 rounded-full transition-all ${currentStep === 2 ? 'bg-signal-ai-secondary' : 'bg-gray-500'
                                    }`} />
                            </div>
                        </div>

                        {/* Next Button */}
                        <button
                            type="button"
                            onClick={handleNext}
                            className="mt-8 w-full rounded-xl py-4 text-sm font-semibold text-white bg-signal-ai-secondary hover:opacity-90 transition"
                        >
                            ရှေ့သို့
                        </button>
                    </div>
                )}

                {/* Section 2 */}
                {currentStep === 2 && (
                    <div className="space-y-8">
                        {/* Gender */}
                        <p className="text-signal-ai-blue font-bold my-4">သွားကျန်းမာရေးစစ်ဆေးခြင်းအတွက် <br />
                            အောက်ပါအချက်အလက်များဖြည့်ပေးပါ။</p>

                        {/* Gender */}
                        <div>
                            <select
                                value={formik.values.gender}
                                onChange={(e) => formik.setFieldValue('gender', e.target.value)}
                                className="w-full rounded-lg bg-signal-ai-secondary text-white border-gray-300 p-3 text-sm focus:border-signal-ai-primary focus:outline-none"
                            >
                                <option value="">ကျား/မ ရွေးချယ်ပါ</option>
                                <option value="male">ကျား</option>
                                <option value="female">မ</option>
                            </select>
                            {getFieldProps('gender').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('gender').error}</p>
                            )}
                        </div>

                        {/* Age */}
                        <div>
                            <input
                                type="number"
                                min="1"
                                max="120"
                                className="w-full rounded-lg bg-signal-ai-secondary placeholder:text-white text-white border-gray-300 p-3 text-sm focus:border-signal-ai-primary focus:outline-none"
                                placeholder="အသက်"
                                value={formik.values.age}
                                onChange={(e) => formik.setFieldValue('age', e.target.value)}
                            />
                            {getFieldProps('age').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('age').error}</p>
                            )}
                        </div>

                        {/* City */}
                        <div>
                            <input
                                type="text"
                                className="w-full rounded-lg bg-signal-ai-secondary placeholder:text-white text-white border-gray-300 p-3 text-sm focus:border-signal-ai-primary focus:outline-none"
                                placeholder="မြို့"
                                value={formik.values.city}
                                onChange={(e) => formik.setFieldValue('city', e.target.value)}
                            />
                            {getFieldProps('city').error && (
                                <p className="mt-1 text-xs text-red-500">{getFieldProps('city').error}</p>
                            )}
                        </div>
                        <p className="text-signal-ai-blue">အချက်အလက်များသည် ကမ်ပိန်းသီးသန့်အတွက်သာဖြစ်ပြီး မည်သည့်နေရာကိုမှမျှဝေခြင်းမပြုပါ။</p>
                        <p className="text-signal-ai-blue">အသေးစိတ်သိရှိလိုပါက Unilever ကိုယ်ရေးအချက်အလက်ဆိုင်ရာ မူဝါဒကို ရေးသားဖော်ပြထားသည့် <a href="https://www.unilevernotices.com/" target="_blank" rel="noopener noreferrer" className="text-signal-ai-blue underline">https://www.unilevernotices.com/</a> တွင် ဝင်ရောက်လေ့လာနိုင်သည်။</p>
                        <p className="text-signal-ai-blue">သဘောတူသည်ဆိုလျှင် ‌သွားကျန်းမာရေးစစ်ဆေးချက်ရယူရန် “သဘောတူပါသည်” ကို နှိပ်ပါ။</p>

                        {/* Step Indicator */}
                        <div className="flex items-center justify-center mt-8 mb-4">
                            <div className="flex items-center gap-4">
                                <div className={`w-3 h-3 rounded-full transition-all ${currentStep === 1 ? 'bg-signal-ai-secondary' : 'bg-gray-500'
                                    }`} />
                                <div className="w-16 h-0.5 bg-gray-500" />
                                <div className={`w-3 h-3 rounded-full transition-all ${currentStep === 2 ? 'bg-signal-ai-secondary' : 'bg-gray-500'
                                    }`} />
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="mt-12 flex-1 rounded-xl py-4 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
                            >
                                နောက်သို့်
                            </button>
                            <button
                                type="submit"
                                disabled={formik.isSubmitting}
                                className="mt-12 flex-1 rounded-xl py-4 text-sm font-semibold text-white bg-signal-ai-secondary hover:opacity-90 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {formik.isSubmitting ? 'Submitting...' : 'သဘောတူသည်'}
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
}
